import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

import { getSubscribeList } from './subscribe';

import type { AppDispatch } from '@/store';

import { firebaseConfig, vapidKey } from '@/constants/env';
import { childSlice } from '@/domain/child/redux';
import { childApplySlice } from '@/domain/childApply/redux';
import { classSlice } from '@/domain/class/redux';
import { uiSlice } from '@/domain/ui/redux';
import { userSlice } from '@/domain/user/redux';
import { api, service } from '@/share';

const startPushSubscription = (username: string) => {
  // ServiceWorker Subscription
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(async (registration) => {
      if (registration.active && Notification.permission === 'granted') {
        // FCM設定
        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);
        const token = await getToken(messaging, {
          serviceWorkerRegistration: registration, vapidKey,
        });
        if (token) {
          const tokenList = await service.device.getTokenList();
          const tokenInDynamoDB = tokenList.find((v: string) => v === token);
          if (tokenInDynamoDB === undefined) {
            // AppSyncでDynamoDBにtokenを登録
            await service.device.createDevice({
              parentId: username,
              createDatetime: new Date().toJSON(),
              token,
            });
          }
        }
        onMessage(messaging, (payload) => {
          // フォアグラウンド時のPush通知の処理を定義
          if (registration.active) {
            if (payload.data) {
              const { title, body, tag } = payload.data;
              registration.active.postMessage({
                title, body, tag, type: 'NOTIFICATION',
              });
            }
          }
        });
      }
    });
  } else {
    // eslint-disable-next-line no-console
    console.error('サービスワーカーをサポートしていないため通知は表示されません。');
  }
};

const startSubscription = async (username: string, dispatch: AppDispatch) => {
  const dt = new Date();
  const token = await api.cognitoApi.getIdJwtToken();
  api.subscriptionClient.connect(
    token,
    getSubscribeList(username, dispatch),
    async (err) => {
      // eslint-disable-next-line no-console
      console.log(err);
      const dt2 = new Date();
      const diffTime = dt2.getTime() - dt.getTime();
      if (diffTime > 5000) {
        await startSubscription(username, dispatch);
      }
    },
  );
};

/**
 * 初期化処理
 * @param dispatch dispatch
 */
export const initialize = async (dispatch: AppDispatch) => {
  const user = await service.user.getUserInfo();
  if (!user.attributes.email_verified) {
    dispatch(userSlice.actions.setUser({
      name: user.username,
      attributes: user.attributes,
    }));
    dispatch(userSlice.actions.setMode('verifyemail'));
    return;
  }

  // ユーザーの設定
  api.wagakoNurseryApi.setParentId(user.username);

  // 初期データ取得
  const childList = await service.child.getChildList();
  dispatch(childSlice.actions.setChildList(childList.map((v) => v.toJsonData())));
  const facilityList = await service.childApply.getFacilityList();
  dispatch(childApplySlice.actions.setFacilityList(facilityList));
  const classChildList = await service.class.getClassChildList();
  dispatch(classSlice.actions.setClassChildList(classChildList.map((v) => v.toJson())));
  const facilityHomepageSettingsList = await service.childApply.getFacilityHomepageSettingsList(
    facilityList,
  );
  dispatch(childApplySlice.actions.setFacilityHomepageSettingsList(facilityHomepageSettingsList));

  // Subscription
  await startSubscription(user.username, dispatch);

  // 通知の許可取得
  if ('Notification' in window) {
    const { permission } = Notification;
    if (permission !== 'denied' && permission !== 'granted') {
      // 通知の許可を求める

      Notification.requestPermission().then(() => {
        startPushSubscription(user.username);
        // eslint-disable-next-line no-new
        new Notification('これからWagakoからの通知を表示します');
      });
    } else {
      startPushSubscription(user.username);
    }
  }
  // 設定
  dispatch(userSlice.actions.setUser({
    name: user.username,
    attributes: user.attributes,
  }));

  // クエリパラメータによるページ遷移
  const querryParam = window.location.search.slice(1);
  if (querryParam === 'target=news') {
    dispatch(uiSlice.actions.setPage('news'));
  } else if (querryParam === 'target=attend' || querryParam === 'target=leave') {
    dispatch(uiSlice.actions.setPage('attendHistory'));
  } else if (querryParam === 'target=photo') {
    dispatch(uiSlice.actions.setPage('photo'));
  } else if (querryParam === 'target=childNotebook') {
    dispatch(uiSlice.actions.setPage('childNotebook'));
  }
  // クエリパラメータ初期化
  window.history.replaceState({ page: 'home' }, '', '/');
  dispatch(userSlice.actions.setMode('home'));
};
